import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Collapse,
	Fade,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Typography,
	useTheme,
} from '@mui/material';
import { generateClient, get, post } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Cache } from 'aws-amplify/utils';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { campaignsByTenant, getContact, getSegment } from 'src/graphql/queries';
import { UserContext } from '../../../contexts/UserContext';
import { ContactPreview } from '../contactView/contactPreview';
import { useDialTransport, WS_STATUS } from './preview/useDialTransport';

export function PreviewDialing(props) {
	const theme = useTheme();
	const client = generateClient();
	const userContext = useContext(UserContext);

	const [loading, setLoading] = useState(true);
	const [allCampaigns, setAllCampaigns] = useState([]);
	const selectedCampaignRef = useRef(null);
	const [selectedCampaign, setSelectedCampaign] = useState(null);
	const [hasRehydratedSelectedCampaign, setHasRehydratedSelectedCampaign] = useState(false);

	const [retrievingContact, setRetrievingContact] = useState(false);
	const [retrievingContactMessage, setRetrievingContactMessage] = useState('');
	const [activeContact, setActiveContact] = useState(null);
	const contactHash = useRef(null);

	const [contactRetrievalExhausted, setContactRetrievalExhausted] = useState(false);

	const [suppressionProfileId, setSuppressionProfileId] = useState('');
	const [allowMultipleDials, setAllowMultipleDials] = useState(false);
	const [allowManualNumberEntry, setAllowManualNumberEntry] = useState(false);

	// Call status state (for ContactPreview integration)
	const [callStatus, setCallStatus] = useState('');
	const [connectionLost, setConnectionLost] = useState(false);

	// Campaign interruption state (paused, ended, mode changed)
	const [campaignInterrupted, setCampaignInterrupted] = useState(false);
	const [interruptionMessage, setInterruptionMessage] = useState('');
	const activeContactRef = useRef(null);
	const campaignInterruptedRef = useRef(false);

	// --- Dial Transport (WS / IoT) ---
	const {
		subscribeToCallStatus,
		performContactAction,
		checkContact,
	} = useDialTransport({
		agentId: props.agentId,
		tenantId: userContext.tenantId,
		apiKey: userContext.apiKey,
		onCallStatusChange: (message) => {
			setCallStatus(message.callStatus || message?.value?.callStatus || '');
			// Propagate active contact state to workspace level
			if (props.onActiveContactChange && activeContactRef.current) {
				const status = message.callStatus || message?.value?.callStatus || '';
				const isTerminal = ['Disconnected', 'Call Ended', 'Error', 'Not Allowed To Dial'].some((t) => status?.includes(t));
				props.onActiveContactChange({
					id: activeContactRef.current.id,
					contactId: activeContactRef.current.id,
					firstName: activeContactRef.current.firstName,
					lastName: activeContactRef.current.lastName,
					phone: activeContactRef.current.phone,
					cell: activeContactRef.current.cell,
					externalId: activeContactRef.current.externalId || '',
					campaignId: activeContactRef.current.campaignId || '',
					segmentId: activeContactRef.current.segmentId || '',
					skillId: selectedCampaignRef.current?.skillId || '',
					lastDialedNumber: activeContactRef.current.phone || activeContactRef.current.cell || '',
					hasCalledOrOnCall: true,
					isCallActive: !isTerminal,
					isLastCallContact: false,
				});
			}
		},
		onAllowDial: null,
		onConnectionStatusChange: (status) => {
			if (status === WS_STATUS.DISCONNECTED) {
				enqueueSnackbar('Connection lost. Attempting to reconnect...', { variant: 'warning', autoHideDuration: 4000 });
				setConnectionLost(true);
			} else if (status === WS_STATUS.CONNECTED) {
				enqueueSnackbar('Connection restored', { variant: 'success', autoHideDuration: 2000 });
				setConnectionLost(false);
			}
		},
	});

	// --- Effects ---

	useEffect(() => {
		if (userContext.apiKey) {
			getCampaigns();
		}
	}, [userContext.apiKey]);

	useEffect(() => {
		setLoading(false);
		if (allCampaigns.length > 0 && !hasRehydratedSelectedCampaign) {
			rehydrateSelectedCampaign();
		}
	}, [allCampaigns]);

	useEffect(() => {
		if (selectedCampaign && !activeContact && !contactHash.current && !callStatus) {
			getContactForDialing();
		}
	}, [selectedCampaign, activeContact]);

	const prevSelectedCampaignIdRef = useRef(null);

	useEffect(() => {
		const previousId = prevSelectedCampaignIdRef.current;
		const currentId = selectedCampaign?.id || null;

		selectedCampaignRef.current = selectedCampaign;
		prevSelectedCampaignIdRef.current = currentId;

		if (hasRehydratedSelectedCampaign) {
			Cache.setItem('previewDialingCampaign', selectedCampaign);
		}

		const previewConfig = JSON.parse(
			selectedCampaign?.profile?.previewConfig || '{}',
		);
		setAllowMultipleDials(previewConfig?.previewAllowMultiDial || false);
		setAllowManualNumberEntry(previewConfig?.allowManualDial || false);

		// Only clear the active contact if the agent deliberately switched campaigns
		// (campaign ID changed). Background data refreshes from subscriptions should
		// NOT disrupt the current contact.
		const campaignIdChanged = previousId !== null && currentId !== previousId;
		if (campaignIdChanged && activeContact) {
			// Recycle the contact back to the queue before clearing
			recycleActiveContact();
			setActiveContact(null);
			contactHash.current = null;
		}

		if (campaignIdChanged || !previousId) {
			setContactRetrievalExhausted(false);
		}
	}, [selectedCampaign]);

	// Keep activeContactRef in sync for use in non-React callbacks (beforeunload, subscription handlers)
	useEffect(() => {
		activeContactRef.current = activeContact;
	}, [activeContact]);

	// --- Campaign state change detection (driven by parent workspace subscription) ---
	// When the parent's campaigns list updates:
	// 1. Sync the campaign dropdown (add new preview campaigns, remove ended/paused ones)
	// 2. Detect if the currently selected campaign was interrupted
	useEffect(() => {
		if (!props.campaigns) return;

		// Filter the parent campaigns list to only preview-eligible ones for this agent
		const previewEligible = props.campaigns.filter((c) => {
			if (c.status !== 'Active') return false;
			if (!c.profile?.previewDialing) return false;
			// Skill-based filtering (NiC and similar — Webex uses team-based filtering
			// handled by the initial getCampaigns fetch, so we only add campaigns the
			// initial fetch already validated)
			if (props.agentSkillIds?.length > 0) {
				return props.agentSkillIds.includes(c.skillId);
			}
			return true;
		});

		// Update the local campaign list
		setAllCampaigns((prev) => {
			const eligibleIds = new Set(previewEligible.map((c) => c.id));
			const prevIds = new Set(prev.map((c) => c.id));

			// Remove campaigns no longer eligible
			let updated = prev.filter((c) => eligibleIds.has(c.id));

			// Update existing campaigns with fresh data
			updated = updated.map((c) => {
				const fresh = previewEligible.find((e) => e.id === c.id);
				return fresh ? { ...c, ...fresh } : c;
			});

			// Add new campaigns that weren't previously in the list
			// (For Webex users, new campaigns will appear after a full page load
			// since team-based filtering requires an API call)
			if (userContext.telephonyProvider !== 'Webex') {
				const newCampaigns = previewEligible.filter((c) => !prevIds.has(c.id));
				updated = [...updated, ...newCampaigns];
			}

			return updated;
		});

		// Now check if the selected campaign was affected
		const currentCampaign = selectedCampaignRef.current;
		if (!currentCampaign) return;

		const isActivelyDialing = activeContactRef.current || retrievingContact || contactHash.current;
		const updatedCampaign = props.campaigns.find((c) => c.id === currentCampaign.id);

		// Campaign no longer in the active list (ended, expired, deleted, or reset)
		if (!updatedCampaign) {
			if (isActivelyDialing) {
				handleCampaignInterruption(
					`Campaign "${currentCampaign.name}" is no longer active. Please dismiss the current contact when you're ready.`,
				);
			} else {
				setSelectedCampaign(null);
			}
			return;
		}

		// Campaign paused
		if (updatedCampaign.status === 'Paused') {
			if (isActivelyDialing) {
				handleCampaignInterruption(
					`Campaign "${updatedCampaign.name}" has been paused. Please dismiss the current contact when you're ready.`,
				);
			} else {
				setSelectedCampaign(null);
			}
			return;
		}

		// Dialing mode changed (previewDialing toggled off)
		if (!updatedCampaign.profile?.previewDialing) {
			if (isActivelyDialing) {
				handleCampaignInterruption(
					`This campaign's dialing mode has changed. You may begin receiving calls if in an available state. Please dismiss the current contact when you're ready.`,
				);
			} else {
				setSelectedCampaign(null);
			}
			return;
		}

		// Campaign still active and still preview — update the ref with fresh data.
		// Never call setSelectedCampaign from subscription updates. The dropdown uses
		// allCampaigns (already updated above), and any API calls use selectedCampaignRef.
		// Calling setSelectedCampaign here would trigger the selectedCampaign effect chain
		// which can clear the active contact or re-fetch contacts unnecessarily.
		selectedCampaignRef.current = { ...currentCampaign, ...updatedCampaign };
	}, [props.campaigns]);

	// --- Navigation away / unmount cleanup ---
	// Recycle the active contact if the agent navigates away or closes the tab
	const recycleActiveContact = useCallback(async () => {
		const contact = activeContactRef.current;
		const hash = contactHash.current;
		const campaign = selectedCampaignRef.current;

		if (!contact || !hash || !campaign) return;

		try {
			const session = await fetchAuthSession();
			await post({
				apiName: 'cdyxoutreach',
				path: '/dial/recycle',
				options: {
					headers: {
						Authorization: `Bearer ${session.tokens.idToken}`,
						'x-api-key': userContext.apiKey,
					},
					body: {
						contactId: contact.id,
						campaignId: campaign.id,
						segmentId: hash.segmentId,
						agent: props.agentId,
						phone: contact.phone || contact.cell || '',
						externalId: contact.externalId || '',
					},
				},
			}).response;
		} catch (error) {
			console.error('Error recycling contact on navigation away', error);
		}
	}, [userContext.apiKey, props.agentId]);

	useEffect(() => {
		// Handle tab close / browser navigation
		const handleBeforeUnload = (event) => {
			if (activeContactRef.current && contactHash.current) {
				// Use sendBeacon for reliable delivery during page unload
				const session = fetchAuthSession();
				// sendBeacon doesn't support auth headers, so fall back to firing the async call
				// The browser may or may not complete it, but it's best-effort
				recycleActiveContact();
			}
		};

		// Handle tab visibility changes (user switches tabs or minimizes)
		// We cache the contact info so it can be recovered if they return quickly
		const handleVisibilityChange = () => {
			if (document.visibilityState === 'hidden' && activeContactRef.current && contactHash.current) {
				// Cache the active contact so it's recoverable
				Cache.setItem('previewDialingActiveContact', {
					contact: activeContactRef.current,
					hash: contactHash.current,
					campaignId: selectedCampaignRef.current?.id,
					timestamp: Date.now(),
				});
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
			document.removeEventListener('visibilitychange', handleVisibilityChange);

			// On unmount (navigating away within the app), recycle the contact
			if (activeContactRef.current && contactHash.current) {
				recycleActiveContact();
			}
		};
	}, [recycleActiveContact]);

	// Subscribe to call status when active contact is present
	useEffect(() => {
		if (props.agentId && activeContact) {
			subscribeToCallStatus();
		}
	}, [props.agentId, activeContact?.id]);

	// --- Dialing handlers ---

	/**
	 * Called when the agent clicks a phone number in ContactPreview.
	 * Performs the contact action (approve) via the dial transport.
	 */
	async function handleApprove(phoneNumber) {
		if (!activeContact || !selectedCampaign || !contactHash.current) return;

		setCallStatus('Initiating Call...');

		// Propagate active contact to workspace level
		if (props.onActiveContactChange) {
			props.onActiveContactChange({
				id: activeContact.id,
				contactId: activeContact.id,
				firstName: activeContact.firstName,
				lastName: activeContact.lastName,
				phone: phoneNumber,
				cell: activeContact.cell,
				externalId: activeContact.externalId || '',
				campaignId: activeContact.campaignId || '',
				segmentId: activeContact.segmentId || '',
				skillId: selectedCampaign.skillId || '',
				lastDialedNumber: phoneNumber,
				hasCalledOrOnCall: true,
				isCallActive: true,
				isLastCallContact: false,
			});
		}

		try {
			await performContactAction({
				type: 'approve',
				contactHash: contactHash.current,
				activeContact,
				phoneNumber,
				campaign: selectedCampaign,
				suppressionProfileId,
			});
		} catch (error) {
			console.error('Error approving contact for dial', error);
			enqueueSnackbar('Error dialing contact', { variant: 'error', autoHideDuration: 3000 });
			setCallStatus('');
		}
	}

	/**
	 * Called when the agent dismisses the call status pane in ContactPreview.
	 */
	function handleResetApprove() {
		setCallStatus('');
	}

	// --- Functions ---

	function handleCampaignInterruption(message) {
		setCampaignInterrupted(true);
		campaignInterruptedRef.current = true;
		setInterruptionMessage(message);
		setRetrievingContact(false);
		setRetrievingContactMessage('');
	}

	async function rehydrateSelectedCampaign() {
		const cachedSelectedCampaign = await Cache.getItem('previewDialingCampaign');
		if (cachedSelectedCampaign) {
			if (!allCampaigns.find((campaign) => campaign.id === cachedSelectedCampaign.id)) {
				await Cache.removeItem('previewDialingCampaign');
			} else {
				setSelectedCampaign(cachedSelectedCampaign);
			}
		}
		setHasRehydratedSelectedCampaign(true);
	}

	async function getCampaigns() {
		try {
			setLoading(true);
			const idToken = (await fetchAuthSession()).tokens.idToken;

			const campaignsResponse = await client.graphql({
				query: campaignsByTenant,
				variables: {
					tenant: idToken.payload['custom:tenant'],
					filter: { status: { eq: 'Active' } },
					limit: 1000,
				},
			});

			const returnedCampaigns = campaignsResponse.data.campaignsByTenant?.items ?? [];

			let filteredCampaigns = returnedCampaigns.filter(
				(campaign) => campaign.profile?.previewDialing,
			);

			if (userContext.telephonyProvider === 'NiC') {
				filteredCampaigns = filteredCampaigns.filter((campaign) =>
					props.agentSkillIds.includes(campaign.skillId),
				);
			} else if (userContext.telephonyProvider === 'Webex') {
				const campaignIdsResponse = await get({
					apiName: 'cdyxoutreach',
					path: '/cci/webex/contact-center/agent-campaigns/' + props.teamId,
					options: {
						headers: {
							Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
							'x-api-key': userContext.apiKey,
						},
					},
				}).response;
				const campaignIdsForTeam = (await campaignIdsResponse.body.json()) ?? [];
				filteredCampaigns = filteredCampaigns.filter((campaign) =>
					campaignIdsForTeam.includes(campaign.id),
				);
			}

			setAllCampaigns(filteredCampaigns);
		} catch (error) {
			console.error(error);
		}
	}

	async function getContactForDialing() {
		selectedCampaignRef.current = selectedCampaign;
		setRetrievingContact(true);
		setRetrievingContactMessage('Getting contact...');
		setContactRetrievalExhausted(false);
		campaignInterruptedRef.current = false;

		const campaignId = selectedCampaign?.id;
		const maxAttempts = 50;
		const retryDelayMs = 1000;

		for (let attempt = 1; attempt <= maxAttempts; attempt++) {
			// Check if campaign changed during retries
			if (selectedCampaignRef.current?.id !== campaignId) {
				setRetrievingContact(false);
				setRetrievingContactMessage('');
				return;
			}

			// Check if campaign was interrupted (paused/ended/mode changed)
			if (campaignInterruptedRef.current) {
				setRetrievingContact(false);
				setRetrievingContactMessage('');
				return;
			}

			try {
				const contactRequest = await get({
					apiName: 'cdyxoutreach',
					path: `/contacts/${selectedCampaign.skillId}`,
					options: {
						headers: {
							Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
							'x-api-key': userContext.apiKey,
						},
						queryParams: { total: 1 },
					},
				}).response;

				let contactHashResponse;
				try {
					contactHashResponse = await contactRequest.body.json();
				} catch (parseError) {
					console.warn(`Attempt ${attempt}: Non-JSON response from contacts API`);
					contactHashResponse = null;
				}

				if (contactHashResponse && contactHashResponse.length > 0 && contactHashResponse[0]) {
					contactHash.current = contactHashResponse[0];
					getSuppressionProfileId(contactHashResponse[0].segmentId);
					let fullContact = await getDynamoContact(contactHashResponse[0].id);
					fullContact.customFields = fullContact.customFields
						? JSON.parse(fullContact.customFields)
						: null;
					// Merge the contact hash (from dispatch/Redis) with the full Dynamo
					// contact. Dynamo data takes precedence for every field EXCEPT
					// outboundCallerId: the caller ID assigned by the caller ID service
					// lives on the contact hash and must persist through to the dial.
					// A caller ID explicitly stored on the Dynamo contact still wins as a
					// fallback when the hash doesn't carry one.
					setActiveContact({
						...contactHashResponse[0],
						...fullContact,
						outboundCallerId:
							contactHashResponse[0].outboundCallerId || fullContact.outboundCallerId || null,
						campaignId: selectedCampaign.id,
						segmentId: contactHashResponse[0].segmentId,
					});
					setRetrievingContact(false);
					setRetrievingContactMessage('');
					return;
				}
			} catch (error) {
				console.error(`Attempt ${attempt}: Error retrieving contact`, error);
			}

			// No contact retrieved — update message based on attempt count
			if (attempt >= 10) {
				setRetrievingContactMessage(
					'This is taking longer than expected. You may want to reach out to your campaign manager.',
				);
			} else if (attempt >= 2) {
				setRetrievingContactMessage('No contact retrieved, retrying...');
			}

			// Wait before retrying (unless this was the last attempt)
			if (attempt < maxAttempts) {
				await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
			}
		}

		// Exhausted all attempts
		setRetrievingContact(false);
		setRetrievingContactMessage('');
		setContactRetrievalExhausted(true);
	}

	async function getSuppressionProfileId(segmentId) {
		try {
			const segmentRequest = await client.graphql({
				query: getSegment,
				variables: { id: segmentId },
			});
			const segmentData = segmentRequest.data.getSegment;
			if (segmentData) {
				setSuppressionProfileId(segmentData.suppressionID);
			}
		} catch (error) {
			console.error('Error getting segment', error);
		}
	}

	async function getDynamoContact(contactId) {
		const contactRequest = await client.graphql({
			query: getContact,
			variables: { id: contactId },
		});
		return contactRequest.data.getContact;
	}

	async function handleDismiss(phoneFieldsAlreadyCalled) {
		const hasDialed = phoneFieldsAlreadyCalled && phoneFieldsAlreadyCalled.length > 0;

		// If no call was made, recycle the contact back to the queue
		if (!hasDialed && activeContact && contactHash.current) {
			try {
				const session = await fetchAuthSession();

				await post({
					apiName: 'cdyxoutreach',
					path: '/dial/recycle',
					options: {
						headers: {
							Authorization: `Bearer ${session.tokens.idToken}`,
							'x-api-key': userContext.apiKey,
						},
						body: {
							contactId: activeContact.id,
							campaignId: selectedCampaign?.id || activeContact.campaignId,
							segmentId: contactHash.current.segmentId,
							agent: props.agentId,
							phone: activeContact.phone || activeContact.cell || '',
							externalId: activeContact.externalId || '',
						},
					},
				}).response;
			} catch (error) {
				console.error('Error recycling contact on dismiss', error);
			}
		}

		// Clear the current contact and hash
		setActiveContact(null);
		contactHash.current = null;
		setCallStatus('');

		// Clear workspace-level active contact
		if (props.onActiveContactChange) {
			props.onActiveContactChange(null);
		}

		// If the campaign was interrupted, clean up interruption state and deselect campaign
		if (campaignInterruptedRef.current) {
			setCampaignInterrupted(false);
			campaignInterruptedRef.current = false;
			setInterruptionMessage('');
			setSelectedCampaign(null);
			await Cache.removeItem('previewDialingCampaign');
		}
	}

	async function handleSuppress() {
		if (!activeContact || !contactHash.current) return;

		try {
			const session = await fetchAuthSession();

			await post({
				apiName: 'cdyxoutreach',
				path: '/dial/suppress',
				options: {
					headers: {
						Authorization: `Bearer ${session.tokens.idToken}`,
						'x-api-key': userContext.apiKey,
					},
					body: {
						contactId: activeContact.id,
						campaignId: selectedCampaign.id,
						segmentId: contactHash.current.segmentId,
						agent: props.agentId,
						phone: activeContact.phone || activeContact.cell || '',
						externalId: activeContact.externalId || '',
					},
				},
			}).response;

			enqueueSnackbar('Contact suppressed', { variant: 'success', autoHideDuration: 2000 });
		} catch (error) {
			console.error('Error suppressing contact', error);
			enqueueSnackbar('Failed to suppress contact', { variant: 'error', autoHideDuration: 3000 });
		}

		// Move on to the next contact
		setActiveContact(null);
		contactHash.current = null;
		setCallStatus('');

		// Clear workspace-level active contact
		if (props.onActiveContactChange) {
			props.onActiveContactChange(null);
		}

		// If the campaign was interrupted, clean up interruption state and deselect campaign
		if (campaignInterruptedRef.current) {
			setCampaignInterrupted(false);
			campaignInterruptedRef.current = false;
			setInterruptionMessage('');
			setSelectedCampaign(null);
			await Cache.removeItem('previewDialingCampaign');
		}
	}

	function handleRetryContactRetrieval() {
		setContactRetrievalExhausted(false);
		getContactForDialing();
	}

	// --- Render ---

	return (
		<Box
			sx={{ width: '100%', backgroundColor: theme.palette.background.default, minHeight: '100%' }}
			display="flex"
			flexDirection="column"
			alignItems="center"
			gap={2}
		>
			<Box
				sx={{ maxWidth: '1200px', width: '100%' }}
				display="flex"
				flexDirection="column"
				alignItems="center"
				gap={2}
			>
				{/* Campaign selector */}
				<Box sx={{ width: '100%', padding: '10px' }} display="flex" gap={2} alignItems="center">
					<FormControl fullWidth>
						<InputLabel>Dialing Campaign</InputLabel>
						<Select
							label="Dialing Campaign"
							value={selectedCampaign ? selectedCampaign.id : ''}
							onChange={(event) => {
								const campaign = allCampaigns.find((c) => c.id === event.target.value);
								setSelectedCampaign(campaign);
								document.activeElement.blur();
							}}
							disabled={allCampaigns.length === 0}
						>
							{allCampaigns.map((campaign) => (
								<MenuItem key={campaign.id} value={campaign.id}>
									{campaign.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Box>

				{/* Loading / Retrieving states */}
				{!selectedCampaign && !loading && (
					<Fade in={true}>
						<Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 200 }}>
							<Typography variant="body1" color="text.secondary">
								Select a campaign to begin dialing
							</Typography>
						</Box>
					</Fade>
				)}

				{retrievingContact && (
					<Fade in={true}>
						<Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ height: 200 }}>
							<CircularProgress />
							<Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
								{retrievingContactMessage}
							</Typography>
						</Box>
					</Fade>
				)}

				{contactRetrievalExhausted && !retrievingContact && (
					<Fade in={true}>
						<Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ height: 200 }}>
							<Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
								Unable to retrieve a contact. You may want to reach out to your campaign manager.
							</Typography>
							<Button variant="contained" onClick={handleRetryContactRetrieval}>
								Retry
							</Button>
						</Box>
					</Fade>
				)}

				{loading && !retrievingContact && (
					<Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 200 }}>
						<CircularProgress />
					</Box>
				)}

				{/* Campaign Interruption Warning Banner */}
				<Collapse in={campaignInterrupted}>
					<Alert
						severity="warning"
						sx={{ width: '100%', mx: '10px' }}
					>
						{interruptionMessage}
					</Alert>
				</Collapse>

				{/* Contact Preview */}
				<Fade in={!!activeContact && !!selectedCampaign}>
					<Box sx={{ width: '100%' }}>
						{activeContact && selectedCampaign && (
							<ContactPreview
								contact={activeContact}
								callStatus={callStatus}
								connectionLost={connectionLost}
								allowManualNumberEntry={allowManualNumberEntry}
								allowMultipleCallsPerContact={allowMultipleDials}
								allowRedial={false}
								dismissMode="dismiss"
								onDismiss={handleDismiss}
								onRecycle={() => handleDismiss([])}
								onSuppress={handleSuppress}
								onApprove={handleApprove}
								onResetApprove={handleResetApprove}
							/>
						)}
					</Box>
				</Fade>
			</Box>
		</Box>
	);
}
